// §34.8 / §34.12 (D-588, D-589) — the place vocabulary, shared because the server and the client
// must agree about it.
//
// A location used to be a latitude and a longitude somebody typed. It is now a **place**: something
// with a name, found by searching for "Hartford, CT" or "KHFD", or read back from a coordinate the
// browser handed over. Two halves of that job live on opposite sides of the wire —
//
//   • the **server** ranks the provider's answers, and needs to know that `CT` and `Connecticut`
//     are the same request;
//   • the **client** draws the label, and needs to know that `Connecticut` renders as `CT`.
//
// — and they have to agree, or a place found by one is a place the other cannot name. So the truth
// table lives here once, in the package that is types and pure functions by construction
// (`package-purity.test.ts`), rather than twice in two files that drift.
//
// Everything in this file is pure. The provider bodies are normalised into `PlaceCandidate` at the
// route, so nothing here has ever seen a third party's schema — the same rule §34.8 already applies
// to the forecast payload, for the same reason.

/**
 * A place, as both sides of the wire know it.
 *
 * `admin1` is the provider's first-order subdivision — a US state, a Canadian province, a French
 * région. Nullable because plenty of the world has none, and a country with no subdivision is not
 * a missing field.
 */
export interface WeatherPlace {
  readonly name: string;
  readonly admin1: string | null;
  readonly country: string;
  /** ISO 3166-1 alpha-2, upper case. `''` when the provider did not say. */
  readonly countryCode: string;
  readonly lat: number;
  readonly lon: number;
  /**
   * `airport` when the provider classified it as one — which is what makes `KHFD` work, and what
   * `rankPlaces` boosts when the query looked like an identifier rather than a name.
   */
  readonly kind: 'airport' | 'place';
}

/** A `WeatherPlace` still carrying the one provider field ranking needs. Server-side only. */
export interface PlaceCandidate extends WeatherPlace {
  /** `0` for anything unpopulated, which is every airport and most landmarks. */
  readonly population: number;
}

/**
 * A search box's contents, taken apart.
 *
 * `region` is the half after the comma — the thing that separates the Hartford in Connecticut from
 * the one in Kentucky. `identifier` is the flag that says the name is probably an airport code.
 */
export interface PlaceQuery {
  /** What the provider is actually asked for. Never empty — `parsePlaceQuery` refuses that. */
  readonly name: string;
  /** `CT` · `Connecticut` · `France` — matched loosely by `matchesRegion`. `null` when absent. */
  readonly region: string | null;
  /** The name is three or four letters and stands alone: `KHFD`, `BDL`, `EGLL`. */
  readonly identifier: boolean;
}

/**
 * First-order subdivisions that have a universally-understood short code, by country.
 *
 * **Only the two countries that actually write addresses this way.** "Hartford, CT" is how an
 * American says it; "London, England" is not how anybody says it, and inventing a two-letter code
 * for Île-de-France would be worse than leaving the country name in place. So this table is
 * deliberately not a world atlas — `formatPlaceLabel` falls back to the country everywhere else,
 * which is both correct and what a reader expects to see.
 *
 * Keys are lower-cased subdivision names as the providers spell them. Québec appears twice on
 * purpose: Open-Meteo and Nominatim do not agree on the accent, and a label is not the place to
 * find that out.
 */
export const SUBDIVISION_CODES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  US: {
    alabama: 'AL',
    alaska: 'AK',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    'district of columbia': 'DC',
    florida: 'FL',
    georgia: 'GA',
    hawaii: 'HI',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY',
    'puerto rico': 'PR',
    guam: 'GU',
    'american samoa': 'AS',
    'northern mariana islands': 'MP',
    'united states virgin islands': 'VI',
    'u.s. virgin islands': 'VI',
  },
  CA: {
    alberta: 'AB',
    'british columbia': 'BC',
    manitoba: 'MB',
    'new brunswick': 'NB',
    'newfoundland and labrador': 'NL',
    'northwest territories': 'NT',
    'nova scotia': 'NS',
    nunavut: 'NU',
    ontario: 'ON',
    'prince edward island': 'PE',
    quebec: 'QC',
    québec: 'QC',
    saskatchewan: 'SK',
    yukon: 'YT',
  },
};

/** `Connecticut` + `US` → `CT`. `null` for a subdivision with no short form anybody writes. */
export function subdivisionCode(admin1: string | null, countryCode: string): string | null {
  if (admin1 === null) return null;
  const table = SUBDIVISION_CODES[countryCode.toUpperCase()];
  return table?.[admin1.trim().toLowerCase()] ?? null;
}

/**
 * The one line a tab, a result row and a geolocated location are all labelled with.
 *
 * `Hartford, CT` · `Hartford-Brainard Airport, CT` · `London, United Kingdom`. The rule is one
 * sentence: a subdivision that has a code gets the code, and everywhere else gets the country —
 * because "Hartford, United States" is useless to an American and "London, England" is odd to
 * everyone. A place with neither is its own name, which is the honest answer for the South Pole.
 */
export function formatPlaceLabel(place: WeatherPlace): string {
  const code = subdivisionCode(place.admin1, place.countryCode);
  if (code !== null) return `${place.name}, ${code}`;
  if (place.country.trim().length > 0 && place.country !== place.name) {
    return `${place.name}, ${place.country}`;
  }
  return place.name;
}

/** A latitude/longitude pair, as a fallback name for a coordinate nothing could name. */
export const formatCoordLabel = (lat: number, lon: number): string =>
  `${lat.toFixed(2)}, ${lon.toFixed(2)}`;

// ── The search box ────────────────────────────────────────────────────────────────────────────

/** Two numbers separated by a comma or a space — the shape, without judging the values. */
const COORD_PAIR = /^\s*(-?\d+(?:\.\d+)?)\s*(?:,\s*|\s+)(-?\d+(?:\.\d+)?)\s*$/;

/**
 * Does this look like somebody typing coordinates rather than a place name?
 *
 * Separate from `parseCoordinatePair` so an **out-of-range** pair can be told apart from a name.
 * `41.74, -999` is a coordinate the user got wrong, and saying so beats searching the world's
 * gazetteer for a string that was never a place — which is what a single nullable parse would do.
 */
export const looksLikeCoordinatePair = (raw: string): boolean => COORD_PAIR.test(raw);

/** The same pair, parsed and range-checked. `null` when it is not one, or not on Earth. */
export function parseCoordinatePair(raw: string): { lat: number; lon: number } | null {
  const match = COORD_PAIR.exec(raw);
  if (match === null) return null;
  const lat = Number(match[1]);
  const lon = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

/** Every region token this file recognises, lower-cased: the codes and the names behind them. */
const REGION_TOKENS: ReadonlySet<string> = new Set(
  Object.values(SUBDIVISION_CODES).flatMap((table) => [
    ...Object.keys(table),
    ...Object.values(table).map((code) => code.toLowerCase()),
  ]),
);

/**
 * Take a search box apart into what to ask for and what to filter by.
 *
 * Three forms, because all three are things a person types:
 *
 *   • `Hartford, CT` — the comma is unambiguous, so anything after it is the region.
 *   • `Hartford CT` — no comma, but the last word is a region token this file knows. **The provider
 *     answers nothing at all for `Hartford CT`** (it is a name search, and no place is named that),
 *     so without this the space form would be a dead end that looks like a broken feature.
 *   • `KHFD` — three or four letters standing alone. Flagged rather than resolved: this file has no
 *     airport table and does not want one, and the provider already indexes the codes as alternate
 *     names. The flag only tells `rankPlaces` which answer to put first.
 *
 * An unrecognised last word stays part of the name — `Salt Lake City` must not lose `City`.
 */
export function parsePlaceQuery(raw: string): PlaceQuery | null {
  const trimmed = raw.trim().replace(/\s+/g, ' ');
  if (trimmed.length === 0) return null;

  const comma = trimmed.indexOf(',');
  if (comma >= 0) {
    const name = trimmed.slice(0, comma).trim();
    const region = trimmed.slice(comma + 1).trim();
    if (name.length === 0) return null;
    return { name, region: region.length > 0 ? region : null, identifier: false };
  }

  const words = trimmed.split(' ');
  const last = words[words.length - 1] ?? '';
  if (words.length > 1 && REGION_TOKENS.has(last.toLowerCase())) {
    return { name: words.slice(0, -1).join(' '), region: last, identifier: false };
  }

  return { name: trimmed, region: null, identifier: /^[a-z]{3,4}$/i.test(trimmed) };
}

/**
 * Is this candidate in the region the query asked for?
 *
 * Deliberately loose — the subdivision by name, the subdivision by code, the country by name and
 * the country by code all count, because `Hartford, CT`, `Hartford, Connecticut` and `Paris,
 * France` are all the same gesture and a user should not have to know which field they hit.
 */
export function matchesRegion(place: WeatherPlace, region: string): boolean {
  const wanted = region.trim().toLowerCase();
  if (wanted.length === 0) return true;
  const code = subdivisionCode(place.admin1, place.countryCode);
  return [place.admin1, code, place.country, place.countryCode].some(
    (field) => field !== null && field.toLowerCase() === wanted,
  );
}

/** How many results a search answers with. Six fits the panel without scrolling it. */
export const PLACE_LIMIT = 6;

/**
 * Rank the provider's candidates into the list the user picks from.
 *
 * Three rules, in this order:
 *
 *   1. **The region filters, unless it matches nothing.** A `region` that no candidate satisfies is
 *      far more likely to have been part of the name than a real filter — somebody searching
 *      `Rio, Brazil` where the provider spells the country `Brasil` should get Rio, not an empty
 *      list. Filtering to nothing is the one outcome that helps nobody.
 *   2. **An identifier query puts airports first.** `BDL` answers with the town of Windsor Locks
 *      *and* Bradley International, in that order, because the town has a population and the
 *      airport does not. Somebody typing three letters meant the airport.
 *   3. **Then population, descending.** It is the only signal the provider gives about which
 *      Hartford anybody means, and it is a good one.
 *
 * Ties break on the name so the order is stable — a list that reshuffles between two identical
 * searches reads as a bug even when both orders are defensible.
 */
export function rankPlaces(
  candidates: readonly PlaceCandidate[],
  query: PlaceQuery,
  limit: number = PLACE_LIMIT,
): WeatherPlace[] {
  const region = query.region;
  const inRegion =
    region === null ? candidates : candidates.filter((candidate) => matchesRegion(candidate, region));
  const pool = inRegion.length > 0 ? inRegion : candidates;

  const seen = new Set<string>();
  return [...pool]
    .sort((a, b) => {
      if (query.identifier) {
        const rank = Number(b.kind === 'airport') - Number(a.kind === 'airport');
        if (rank !== 0) return rank;
      }
      if (b.population !== a.population) return b.population - a.population;
      return a.name.localeCompare(b.name);
    })
    .filter((candidate) => {
      // Two entries at the same spot are one place the provider listed twice; the higher-ranked
      // one has already won, so the second is noise in a list of six.
      const key = `${candidate.lat.toFixed(2)},${candidate.lon.toFixed(2)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit)
    .map(({ population: _population, ...place }) => place);
}
